/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import createResource, {
    IResourceModel,
    IResourceId,
    IResourceProps,
    IResourceStore,
    IResourceMap,
} from 'apmui/resource';
import Object from 'apmui/util/Object';

import ResourceType from './ResourceType';
import { DEFAULT_SECTION_ID } from './webtopSection/createDefaultSections';
import WebtopSectionModel from './webtopSection/Model';
import { collectFolderIds, createResourceProps } from 'apmui/resource/webtopFolder/Folder';

function resourceIdEquals(a: IResourceId, b: IResourceId) {
    return a.type === b.type && a.id === b.id;
}

function addUniqueResourceIds(dst: IResourceId[], src: IResourceId[], position = -1) {
    const unique = src.filter((x) => !dst.some((y) => resourceIdEquals(x, y)));
    if (position >= 0) {
        dst.splice(position, 0, ...unique);
    } else {
        dst.push(...unique);
    }
}

/**
 * Check that `res` is already initialized resource object.
 */
function isResource(res?: object): res is IResourceModel {
    return !!res && 'type' in res && 'id' in res;
}

/**
 *  Add multiple resources into store.
 *
 * @param resources Resources or resource constructor parameters
 * @param change    (optional) Existing object with updated resources
 * @returns object with all updated resources
 */
function putResources(
    store: IResourceStore,
    resources: IResourceMap | Array<IResourceModel | IResourceProps>,
    change: IResourceStore = {}
): IResourceStore {
    if (Array.isArray(resources)) {
        for (const resource of resources) {
            const newResource = isResource(resource) ? resource : createResource(resource.props.id, resource);
            if (!newResource) {
                continue;
            }
            if (!change[newResource.type]) {
                change[newResource.type] = {};
            }
            if (!store[newResource.type]) {
                store[newResource.type] = {};
            }
            change[newResource.type]![newResource.id] = newResource;
            store[newResource.type]![newResource.id] = newResource;
        }
    } else {
        for (const group of Object.values(resources)) {
            if (!group || !group.resources) {
                continue;
            }
            putResources(store, Object.values(group.resources), change);
        }
    }
    return change;
}

/**
 * Create missing webtop folder resources and update assignedResources for existing ones.
 * @param store Webtop resources store
 * @param newResources resources to be added
 * @returns ids of all modified folders
 */
function createOrUpdateFolders(store: IResourceStore, newResources: IResourceMap): IResourceId[] {
    const folders = collectFolderIds(newResources);
    const existingFolderResources = store[ResourceType.WEBTOP_FOLDER];

    if (!newResources[ResourceType.WEBTOP_FOLDER] || !newResources[ResourceType.WEBTOP_FOLDER]!.resources) {
        newResources[ResourceType.WEBTOP_FOLDER] = { param: '', resources: {} };
    }
    const newFolderResources = newResources[ResourceType.WEBTOP_FOLDER]!.resources!;

    const folderIds = Object.entries(folders).map(([folder, resources]) => {
        if (existingFolderResources && existingFolderResources[folder]) {
            addUniqueResourceIds(existingFolderResources[folder].assignedResources, resources);
        } else {
            newFolderResources[folder] = createResourceProps(folder, resources);
        }
        return { type: ResourceType.WEBTOP_FOLDER, id: folder };
    });
    return folderIds;
}

/**
 * Add specified resources to the end of specified sections or to all the sections containing `template`.
 * If the `template` is given and target section has PRIORITY sort type,
 * newly assigned resources will be displayed right after the `template`.
 *
 * If no template or sections specified, resources will be assigned to default sections.
 *
 * @param resources Resource IDs to be assigned to sections
 * @param sections List of target section IDs or resource ID that provides section list and positions.
 */
function assignToSections(store: IResourceStore, resources: IResourceId[], sections?: string[]) {
    const configuredSections = store[ResourceType.WEBTOP_SECTION];
    if (!configuredSections) {
        return;
    }

    if (Array.isArray(sections) && sections.length) {
        sections
            .map((name) => configuredSections[name])
            .filter((section) => Boolean(section))
            .forEach((section) => addUniqueResourceIds(section.assignedResources, resources));
    } else {
        const naFallback = configuredSections[DEFAULT_SECTION_ID.LEGACY_NA];
        const fallback =
            configuredSections[DEFAULT_SECTION_ID.LEGACY_OTHER] || configuredSections[DEFAULT_SECTION_ID.FALLBACK];

        if (naFallback) {
            addUniqueResourceIds(
                naFallback.assignedResources,
                resources.filter((res) => res.type === ResourceType.NETWORK_ACCESS)
            );
            addUniqueResourceIds(
                fallback.assignedResources,
                resources.filter((res) => res.type !== ResourceType.NETWORK_ACCESS)
            );
        } else {
            addUniqueResourceIds(fallback.assignedResources, resources);
        }
    }
}

/**
 * Assign resources into all the sections containing `template` resource at the same position.
 * For the section with PRIORITY sort type, newly assigned resources will be displayed
 * right after the `template`.
 *
 * @param resources Resource IDs that will be assigned to sections
 * @param template   Resource ID that will be used as a reference point
 */
function assignAtPosition(store: IResourceStore, resources: IResourceId[], template: IResourceId) {
    for (const [section, idx] of getSectionsWithResource(store, template)) {
        addUniqueResourceIds(section.assignedResources, resources, idx);
    }
}

/**
 * Find all sections containing specified resource
 *
 * @returns list of tuples with section and resource index in `section.assignedResources`.
 */
function getSectionsWithResource(store: IResourceStore, resource: IResourceId): [WebtopSectionModel, number][] {
    const result: [WebtopSectionModel, number][] = [];
    const configuredSections = store[ResourceType.WEBTOP_SECTION];
    if (!configuredSections) {
        return result;
    }
    for (const section of Object.values(configuredSections)) {
        for (let idx = 0; idx < section.assignedResources.length; ++idx) {
            if (resourceIdEquals(section.assignedResources[idx], resource)) {
                result.push([section, idx]);
            }
        }
    }
    return result;
}

/**
 * Remove resource from specified sections.
 *
 * @remarks
 * Note that the resource will be hidden from sections but remain in configuration.
 *
 * @param resourceIds  Resources to be removed
 * @param sections  List of sections that should not contain the resource. Omit parameter to remove from all sections.
 */
function removeFromSections(store: IResourceStore, resourceIds: IResourceId[], sections?: string[]) {
    for (const resource of resourceIds) {
        for (const [section, idx] of getSectionsWithResource(store, resource)) {
            if (!sections || sections.indexOf(section.id) >= 0) {
                section.assignedResources.splice(idx, 1);
            }
        }
    }
}

export {
    addUniqueResourceIds,
    assignAtPosition,
    assignToSections,
    createOrUpdateFolders,
    isResource,
    putResources,
    removeFromSections,
    resourceIdEquals,
};
